import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { ItemDialog } from "./ItemDialog";
import { fmtCost, fmtQuantity, labelOf, scMessage } from "./scUtils";

const I = OPS.sc.items;
const ALL = "all";

/** Group names in the server's display order (scope.labels.groups), then any others A→Z. */
export function orderedGroups(scope, items) {
    const known = scope?.labels?.groups || [];
    const extra = [...new Set((items || []).map((i) => i.group).filter((g) => g && !known.includes(g)))].sort();
    return [...known, ...extra];
}

/** Loads every item (inactive included) once; filters are client side. Shared
 *  with the costs tab, which opens the same dialog from its coverage list;
 *  `load()` resolves to the fresh list (null on failure). */
export function useScItems() {
    const { lang } = useI18n();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get("/sc/items", { params: { include_inactive: 1 } });
            const list = r.data?.items || [];
            setItems(list);
            return list;
        } catch (e) {
            const msg = scMessage(e, lang);
            if (msg) toast.error(msg); else setFailed(true);
            return null;
        } finally {
            setLoading(false);
        }
    }, [lang]);
    return { items, loading, failed, load };
}

/** «الأصناف» (finance): code / ERP / name / unit / pack / kinds / manual cost / active. */
export function ItemsAdmin({ scope }) {
    const { t, tn, lang } = useI18n();
    const { items, loading, failed, load } = useScItems();
    const [q, setQ] = useState("");
    const [group, setGroup] = useState(ALL);
    const [showInactive, setShowInactive] = useState(false);
    const [editing, setEditing] = useState(null);   // item | {} for create | null

    useEffect(() => { load(); }, [load]);

    const groups = useMemo(() => orderedGroups(scope, items), [scope, items]);
    // the filtered group's last item moved to another group: the filter would keep
    // a value the select no longer offers (blank trigger, empty list)
    useEffect(() => { if (group !== ALL && !groups.includes(group)) setGroup(ALL); }, [groups, group]);
    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const rank = (g) => { const i = groups.indexOf(g); return i === -1 ? groups.length : i; };
        return items
            .filter((r) => {
                if (!showInactive && r.is_active === false) return false;
                if (group !== ALL && r.group !== group) return false;
                if (!needle) return true;
                return [r.code, r.erp_code, r.name_ar, r.name_en].some((v) => (v || "").toLowerCase().includes(needle));
            })
            .sort((a, b) => rank(a.group) - rank(b.group) || (a.sort_order || 0) - (b.sort_order || 0)
                || (a.name_ar || "").localeCompare(b.name_ar || "", "ar"));
    }, [items, q, group, showInactive, groups]);
    const counts = useMemo(() => ({ total: items.length, active: items.filter((r) => r.is_active !== false).length }), [items]);
    const n = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);
    const kindLabels = scope?.labels?.kinds;
    const th = "px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]";

    return (
        <div data-testid={I.view} className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <div className="relative min-w-[12rem] flex-1">
                    <Search className="pointer-events-none absolute start-2.5 top-3 h-4 w-4 text-[color:var(--ink-faint)]" />
                    <Input data-testid={I.search} value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder={t("stk_items_search")} className="h-10 w-full ps-8 text-base" />
                </div>
                <Select value={group} onValueChange={setGroup}>
                    <SelectTrigger data-testid={I.group} className="h-10 min-w-[9rem] flex-1 sm:w-48 sm:flex-none">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>{t("stk_items_all_groups")}</SelectItem>
                        {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <Switch data-testid={I.showInactive} checked={showInactive} onCheckedChange={setShowInactive} />
                    {t("stk_items_show_inactive")}
                </label>
                <Badge variant="secondary" className="h-7 px-2.5 text-[11px]">
                    {t("stk_items_count")}: <bdi dir="ltr" className="ms-1">{n(counts.active)}/{n(counts.total)}</bdi>
                </Badge>
                <Button data-testid={I.add} size="sm" onClick={() => setEditing({})} className="ms-auto h-9 gap-1.5 text-xs font-semibold">
                    <Plus className="h-4 w-4" />{t("stk_items_add")}
                </Button>
            </div>

            {failed ? (
                <div data-testid={OPS.sc.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />{t("stk_admin_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={OPS.sc.retry} onClick={load}>{t("stk_admin_retry")}</Button>
                </div>
            ) : null}

            {loading && !items.length ? <InlineLoader /> : !shown.length ? (
                <p data-testid={I.empty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("stk_items_empty")}
                </p>
            ) : (
                <div className={`overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                    <table className="w-full text-sm">
                        <thead className="bg-[color:var(--bg-surface-2)]">
                            <tr>
                                <th className={`${th} hidden sm:table-cell`}>{t("stk_items_code")}</th>
                                <th className={`${th} hidden md:table-cell`}>{t("stk_items_erp_code")}</th>
                                <th className={th}>{t("stk_items_name")}</th>
                                <th className={th}>{t("stk_items_unit")}</th>
                                <th className={`${th} hidden md:table-cell`}>{t("stk_items_pack")}</th>
                                <th className={`${th} hidden sm:table-cell`}>{t("stk_items_kinds")}</th>
                                <th className={th}>{t("stk_items_cost")}</th>
                                <th className={`${th} hidden sm:table-cell`}>{t("stk_items_active")}</th>
                                <th className={th}><span className="sr-only">{t("stk_items_edit")}</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((r) => {
                                const pack = r.pack_unit && r.pack_size
                                    ? <>{r.pack_unit} = <bdi dir="ltr">{fmtQuantity(r.pack_size, lang)}</bdi> {r.count_unit}</> : "—";
                                return (
                                    <tr key={r.id} data-testid={I.row(r.id)} data-active={r.is_active === false ? "false" : "true"}
                                        className={`border-t border-[color:var(--separator)] ${r.is_active === false ? "opacity-60" : ""}`}>
                                        <td className="hidden px-3 py-2.5 text-xs tabular-nums text-[color:var(--ink-muted)] sm:table-cell">
                                            <bdi dir="ltr">{r.code}</bdi>
                                        </td>
                                        <td className="hidden px-3 py-2.5 text-xs tabular-nums text-[color:var(--ink-muted)] md:table-cell">
                                            {r.erp_code ? <bdi dir="ltr">{r.erp_code}</bdi> : "—"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className="block font-semibold text-[color:var(--ink)]">{tn(r)}</span>
                                            <span className="block text-[11px] text-[color:var(--ink-faint)]">
                                                <span className="sm:hidden"><bdi dir="ltr">{r.code}</bdi>{r.erp_code ? <> · <bdi dir="ltr">{r.erp_code}</bdi></> : null} · </span>
                                                {r.group}
                                                {r.is_active === false ? <span className="sm:hidden"> · {t("stk_admin_inactive")}</span> : null}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-[color:var(--ink)]">
                                            <span className="block">{r.count_unit}</span>
                                            {r.pack_unit ? <span className="block text-[11px] text-[color:var(--ink-faint)] md:hidden">{pack}</span> : null}
                                        </td>
                                        <td className="hidden px-3 py-2.5 text-xs text-[color:var(--ink-muted)] md:table-cell">{pack}</td>
                                        <td className="hidden px-3 py-2.5 sm:table-cell">
                                            <span className="flex flex-wrap gap-1">
                                                {(r.kinds || []).map((k) => (
                                                    <Badge key={k} variant="secondary" className="px-1.5 py-0 text-[10px]">{labelOf(kindLabels, k, lang)}</Badge>
                                                ))}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-xs tabular-nums text-[color:var(--ink)]">
                                            {r.manual_cost === null || r.manual_cost === undefined ? <span className="text-[color:var(--ink-faint)]">—</span>
                                                : <bdi dir="ltr">{fmtCost(r.manual_cost, lang)}</bdi>}
                                        </td>
                                        <td className="hidden px-3 py-2.5 sm:table-cell">
                                            <span className={`text-[11px] font-bold ${r.is_active === false ? "text-[color:var(--ink-faint)]" : "text-[color:var(--success)]"}`}>
                                                {t(r.is_active === false ? "stk_admin_inactive" : "stk_admin_active")}
                                            </span>
                                        </td>
                                        <td className="px-1 py-2.5 text-end">
                                            <Button data-testid={I.edit(r.id)} variant="ghost" size="sm" onClick={() => setEditing(r)}
                                                    aria-label={t("stk_items_edit")} className="h-9 gap-1 px-2 text-[11px]">
                                                <Pencil className="h-3.5 w-3.5" />
                                                <span className="hidden sm:inline">{t("stk_items_edit")}</span>
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {editing ? (
                <ItemDialog item={editing} scope={scope} groups={groups}
                            onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); load(); }} />
            ) : null}
        </div>
    );
}
